import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { Calendar, Package, Clock, TrendingUp, AlertTriangle, CheckCircle } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useAppStore } from "@/lib/store";

// Analytics interface matching the API response
interface PackageAnalytics {
  totalPackages: number;
  addendumPackages: number;
  draftPackages: number;
  sentPackages: number;
  viewedPackages: number;
  downloadedPackages: number;
  returnedPackages: number;
  awardedPackages: number;
}

export default function TenderAnalyticsSummary() {
  const { selectedProject } = useAppStore();
  
  // Debug: Show current project data
  console.log('🔍 TenderAnalyticsSummary - Selected Project:', selectedProject);
  
  // Use the same analytics endpoint as PackageAnalyticsChart for consistency
  const { data: analytics, isLoading, error } = useQuery<PackageAnalytics>({
    queryKey: ["package-analytics", selectedProject?.id],
    queryFn: () => apiRequest(`/api/package-analytics${selectedProject?.id ? `?projectId=${selectedProject.id}` : ''}`),
    enabled: !!selectedProject?.id,
    refetchInterval: 10000, // Refresh every 10 seconds to sync with activity updates
    refetchOnWindowFocus: true, // Refresh when window gains focus
    staleTime: 5000, // Consider data stale after 5 seconds
    retry: 1, // Only retry once on failure
  });

  // Get packages data only for deadline calculations
  const { data: packagesData } = useQuery({
    queryKey: ["/api/tender-packages", selectedProject?.id],
    queryFn: () => apiRequest(`/api/tender-packages${selectedProject?.id ? `?projectId=${selectedProject.id}` : ''}`),
    enabled: !!selectedProject?.id,
    refetchInterval: 10000, // Refresh every 10 seconds to sync with activity updates
    refetchOnWindowFocus: true, // Refresh when window gains focus
    staleTime: 5000, // Consider data stale after 5 seconds
    retry: 1, // Only retry once on failure
  });

  // Calculate deadline analytics from packages data
  const getDeadlineAnalytics = () => {
    if (!packagesData) return { packagesNext7Days: 0, packagesNext14Days: 0 };

    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const fourteenDaysFromNow = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);

    const packages = Array.isArray(packagesData) ? packagesData : [];

    // Count unique packages with deadlines in next 7 and 14 days
    const packagesNext7Days = packages.filter((p: any) => {
      if (!p.submissionDeadline) return false;
      const deadline = new Date(p.submissionDeadline);
      return deadline >= now && deadline <= sevenDaysFromNow;
    });

    const packagesNext14Days = packages.filter((p: any) => {
      if (!p.submissionDeadline) return false;
      const deadline = new Date(p.submissionDeadline);
      return deadline >= now && deadline <= fourteenDaysFromNow;
    });

    return {
      packagesNext7Days: packagesNext7Days.length,
      packagesNext14Days: packagesNext14Days.length
    };
  };

  const deadlineAnalytics = getDeadlineAnalytics();

  // Show default empty state instead of loading animation when there's no data
  if (isLoading && !analytics) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-3">
              <div className="h-6 bg-gray-200 animate-pulse rounded w-2/3" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="h-8 bg-gray-200 animate-pulse rounded w-1/2" />
                <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Use default empty data if API fails or returns no data
  const safeAnalytics = analytics || {
    totalPackages: 0,
    addendumPackages: 0,
    draftPackages: 0,
    sentPackages: 0,
    viewedPackages: 0,
    downloadedPackages: 0,
    returnedPackages: 0,
    awardedPackages: 0
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {/* Upcoming Deadlines - Next 7 Days */}
      <Card className="border-orange-200 bg-orange-50">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-orange-800 text-base">
            <AlertTriangle className="w-4 h-4" />
            Next 7 Days
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-3">
          <div className="text-2xl font-bold text-orange-900">
            {deadlineAnalytics?.packagesNext7Days || 0}
          </div>
          <div className="text-xs text-orange-600 mt-1">Tender deadlines</div>
        </CardContent>
      </Card>

      {/* Upcoming Deadlines - Next 14 Days */}
      <Card className="border-yellow-200 bg-yellow-50">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-yellow-800 text-base">
            <Clock className="w-4 h-4" />
            Next 14 Days
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-3">
          <div className="text-2xl font-bold text-yellow-900">
            {deadlineAnalytics?.packagesNext14Days || 0}
          </div>
          <div className="text-xs text-yellow-600 mt-1">Tender deadlines</div>
        </CardContent>
      </Card>

      {/* Package Overview */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-blue-800 text-base">
            <Package className="w-4 h-4" />
            Packages
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-3">
          <div className="text-2xl font-bold text-blue-900">
            {(analytics?.totalPackages || 0) - (analytics?.addendumPackages || 0)}/{analytics?.totalPackages || 0}
          </div>
          <div className="text-xs text-blue-600 mt-1">
            Main packages ({(analytics?.totalPackages || 0) - (analytics?.addendumPackages || 0)} main + {analytics?.addendumPackages || 0} addendums)
          </div>
        </CardContent>
      </Card>

      {/* Addendum Packages */}
      <Card className="border-green-200 bg-green-50">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-green-800 text-base">
            <TrendingUp className="w-4 h-4" />
            Addendums
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-3">
          <div className="text-2xl font-bold text-green-900">
            {analytics?.addendumPackages || 0}
          </div>
          <div className="text-xs text-green-600 mt-1">Addendum packages</div>
        </CardContent>
      </Card>
    </div>
  );
}